import { useCallback, useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * High-quality voiceover hook backed by OpenAI TTS via /api/tts.
 * - Fetches audio as a Blob, plays via HTMLAudioElement
 * - Falls back to Web Speech API if backend TTS fails
 * - Caches audio per (lang, text) so re-clicks are instant
 */
const localCache = new Map();

function cacheKey(text, lang, voice) {
  return `${lang}::${voice}::${text}`;
}

export function useVoiceOver() {
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      try {
        window.speechSynthesis?.cancel();
      } catch {}
    };
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    try {
      window.speechSynthesis?.cancel();
    } catch {}
    setSpeaking(false);
    setLoading(false);
  }, []);

  const playFromUrl = useCallback((url) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(url);
    audio.preload = "auto";
    audio.onplay = () => setSpeaking(true);
    audio.onended = () => setSpeaking(false);
    audio.onerror = () => setSpeaking(false);
    audio.onpause = () => {
      // do not flip speaking — pause can happen mid-stream
    };
    audioRef.current = audio;
    audio.play().catch(() => setSpeaking(false));
  }, []);

  const speakViaBrowser = useCallback((text, lang) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.cancel();
    } catch {}
    const u = new SpeechSynthesisUtterance(text);
    const langMap = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-US" };
    u.lang = langMap[lang] || "de-CH";
    u.rate = 0.97;
    u.pitch = 1.05;
    u.onstart = () => setSpeaking(true);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.resume();
    window.speechSynthesis.speak(u);
  }, []);

  const speak = useCallback(
    async (text, lang = "de") => {
      if (!text) return;
      const voice = "shimmer"; // bright, soft, flowing female voice
      const key = cacheKey(text, lang, voice);

      // Optimistic UI
      setSpeaking(true);
      setLoading(true);

      // Cache hit
      const cached = localCache.get(key);
      if (cached) {
        setLoading(false);
        playFromUrl(cached);
        return;
      }

      try {
        const res = await fetch(`${API}/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text,
            language: lang,
            voice,
            model: "tts-1-hd",
            speed: 1.0,
          }),
        });
        if (!res.ok) throw new Error(`TTS ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        localCache.set(key, url);
        setLoading(false);
        playFromUrl(url);
      } catch (err) {
        // Fallback to browser TTS
        setLoading(false);
        speakViaBrowser(text, lang);
      }
    },
    [playFromUrl, speakViaBrowser]
  );

  return { speak, stop, speaking, loading, supported: true };
}
